import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Clock, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PageHero from '@/components/common/PageHero';
import { useStore } from '@/contexts/StoreContext';
import { useAvatar } from '@/hooks/useAvatar';
import { useTextosDaLoja } from '@/construtor/textos';
import {
  playersOnlineService,
  temPaginaDeJogadores,
  tempoDeJogo,
  type PlayTimeRanking,
  type PlayersOnline,
} from '@/lib/services/players-online.service';

/**
 * Vitrine > Jogadores: quem está jogando agora, por servidor, e o ranking de
 * tempo de jogo (do mês e de sempre).
 *
 * Location: src/pages
 * Why: o plugin conta quem está no servidor a cada minuto e a loja mostra.
 * A página só existe em loja de jogo e com a opção ligada no painel (a API
 * também confere: desligada, responde que não existe).
 */
export default function PlayersPage() {
  const { store } = useStore();
  const { t } = useTextosDaLoja();
  const { getAvatarUrl } = useAvatar();
  const [online, setOnline] = useState<PlayersOnline | null>(null);
  const [periodo, setPeriodo] = useState<'month' | 'all'>('month');
  const [ranking, setRanking] = useState<PlayTimeRanking | null>(null);
  const ativa = temPaginaDeJogadores(store);

  useEffect(() => {
    if (!store?.id || !ativa) return;
    const buscar = () =>
      playersOnlineService
        .online(store.id)
        .then(setOnline)
        .catch(() => setOnline({ total: 0, servers: [] }));
    buscar();
    const vez = window.setInterval(buscar, 60_000);
    return () => window.clearInterval(vez);
  }, [store?.id, ativa]);

  useEffect(() => {
    if (!store?.id || !ativa) return;
    setRanking(null);
    playersOnlineService
      .ranking(store.id, periodo)
      .then(setRanking)
      .catch(() => setRanking({ period: periodo, players: [] }));
  }, [store?.id, ativa, periodo]);

  if (store && !ativa) return <Navigate to="/" replace />;

  const cabeca = (nick: string) => (
    <Avatar className="h-10 w-10 rounded-md">
      <AvatarImage src={getAvatarUrl(nick, 64)} alt="" />
      <AvatarFallback className="rounded-md">{nick.slice(0, 2).toUpperCase()}</AvatarFallback>
    </Avatar>
  );

  return (
    <div>
      <PageHero title={t('store.players_page.title')} breadcrumbs={[{ label: t('store.players_page.title') }]} />
      <p className="container mx-auto px-4 pt-8 text-sm text-muted-foreground md:px-6 lg:px-8">{t('store.players_page.subtitle')}</p>
      <div className="container mx-auto grid gap-6 px-4 py-8 md:px-6 lg:grid-cols-[1.4fr_1fr] lg:px-8">
        <Card className="border-border/40" data-testid="players-online">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              {t('store.players_page.online_title', { count: online?.total ?? 0 })}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {online && online.total === 0 && <p className="text-sm text-muted-foreground">{t('store.players_page.online_empty')}</p>}
            {online?.servers
              .filter((s) => s.count > 0 || online.servers.length > 1)
              .map((s) => (
                <div key={s.name} className="space-y-3">
                  {online.servers.length > 1 && (
                    <p className="text-sm font-semibold">
                      {s.name}{' '}
                      <span className="font-normal text-muted-foreground tabular-nums">
                        {s.max ? `${s.count}/${s.max}` : s.count}
                      </span>
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3">
                    {s.players.map((p) => (
                      <div key={p.nick} className="flex items-center gap-2 rounded-lg border border-border/40 bg-card/60 px-2 py-1.5" data-testid="player-online">
                        {cabeca(p.nick)}
                        <span className="text-sm font-medium">{p.nick}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </CardContent>
        </Card>

        <Card className="border-border/40" data-testid="play-time-ranking">
          <CardHeader className="space-y-3">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              {t('store.players_page.ranking_title')}
            </CardTitle>
            <Tabs value={periodo} onValueChange={(v) => setPeriodo(v as 'month' | 'all')}>
              <TabsList>
                <TabsTrigger value="month">{t('store.players_page.month')}</TabsTrigger>
                <TabsTrigger value="all">{t('store.players_page.all_time')}</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent className="space-y-2">
            {ranking && ranking.players.length === 0 && <p className="text-sm text-muted-foreground">{t('store.players_page.ranking_empty')}</p>}
            {ranking?.players.map((p, i) => (
              <div key={p.nick} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-muted/40" data-testid="play-time-row">
                <span className="w-6 text-center text-sm font-bold tabular-nums text-muted-foreground">{i + 1}</span>
                {cabeca(p.nick)}
                <span className="flex-1 text-sm font-medium">{p.nick}</span>
                <span className="text-sm tabular-nums text-muted-foreground">{tempoDeJogo(p.seconds)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
